import { type FC } from 'react';
import { Outlet } from 'react-router-dom';
import { Layout } from 'antd';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';

const { Content } = Layout;

interface MainLayoutProps {
  isDark: boolean;
  onToggleTheme: () => void;
}

export const MainLayout: FC<MainLayoutProps> = ({ isDark, onToggleTheme }) => (
  <Layout style={{ minHeight: '100vh' }}>
    {/*
      Liquid-glass SVG filter — referenced by .navbar-pill's backdrop-filter
      via `url(#liquid-glass-refraction)`. feTurbulence + feDisplacementMap
      gently bends the backdrop through the pill the way Apple's iOS 26
      "Liquid Glass" material refracts light through a curved surface.
      Lives at the layout root so the filter ID is reachable from anywhere
      in the page.
    */}
    <svg
      width="0"
      height="0"
      style={{ position: 'absolute', pointerEvents: 'none' }}
      aria-hidden="true"
    >
      <defs>
        <filter
          id="liquid-glass-refraction"
          x="-10%"
          y="-10%"
          width="120%"
          height="120%"
          colorInterpolationFilters="sRGB"
        >
          {/* Fractal noise — the "shape" of the glass surface */}
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.012 0.018"
            numOctaves="2"
            seed="7"
            result="noise"
          />
          {/* Soften the noise so refraction is fluid, not pixelated */}
          <feGaussianBlur in="noise" stdDeviation="1.2" result="softNoise" />
          {/* Bend the backdrop along the noise map */}
          <feDisplacementMap
            in="SourceGraphic"
            in2="softNoise"
            scale="14"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
    </svg>

    <Navbar isDark={isDark} onToggleTheme={onToggleTheme} />
    {/* Navbar floats ON TOP of content (glass pill style) — content starts at
        viewport top so the hero can fill 100vh and the pill's backdrop-blur
        has actual content to blur. Top spacing for non-hero pages is provided
        by `.section { padding: 96px 0 }` which clears the floating pill. */}
    <Content style={{ flex: '1 0 auto', minHeight: '100vh' }}>
      <Outlet />
    </Content>
    <Footer />
  </Layout>
);
